import type { VisualGuidance } from '@toolproof-core/visualization/_contract/guidance';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { conceptPresentationAt } from '@/_pure/episode';
import { createEpisodePresentationRequest, episodePresentationAnchorIndex, selectTraceEpisodePresentation } from '@/_pure/episodePresentation';
import { resolvePrimitiveConceptPresentation, resolvePrimitivePresentation, validatePrimitivePresentation } from '@/adapters/primitive/_pure/derivation/presentation';
import type { EpisodePlayback } from '@/_contract/episode';
import type { TraceReplay } from '@/_contract/trace';
import type { PrimitiveEmphasisConfig } from '@/adapters/primitive/_contract/config';
import type { PrimitiveConceptBeat } from '@/adapters/primitive/_contract/episode';
import type { PrimitivePresentationContext } from '@/adapters/primitive/_contract/presentation';
import type { PrimitiveScenePlan as ScenePlan } from '../../_contract/scenePlan';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

export function derivePrimitiveEpisodeGuidance(args: { plan: ScenePlan; replay: TraceReplay;
    playback: EpisodePlayback; committedStepIndex: number; openingStepIndex?: number; steps: readonly PrimitiveStepState[];
    frameAt: (index: number) => SpaceDerivationFrame; emphasis: PrimitiveEmphasisConfig }): VisualGuidance | undefined {
    const { playback, steps } = args;
    const selection = selectTraceEpisodePresentation(args);
    if (!selection) return undefined;
    const { selected, anchorIndex } = selection;
    const frame = args.frameAt(anchorIndex);
    const wheels = steps[anchorIndex]?.keyframes.at(-1)?.wheels;
    if (!wheels) throw new Error('Episode guidance lost its anchor arrangement.');
    return resolvePrimitivePresentation({
        ...createEpisodePresentationRequest(selected, playback, args.committedStepIndex === anchorIndex),
        frame, wheels, emphasis: args.emphasis,
    });
}

export function validatePrimitiveEpisodeTargets(plan: ScenePlan, replay: TraceReplay,
    steps: readonly PrimitiveStepState[], frameAt: (index: number) => SpaceDerivationFrame, emphasis: PrimitiveEmphasisConfig,
    openingStepIndex = 0): void {
    for (const [beatIndex, beat] of plan.beats.entries()) {
        for (const segment of beat.presentation ?? []) {
            const index = episodePresentationAnchorIndex(plan, replay, beatIndex, segment, openingStepIndex);
            const wheels = steps[index]?.keyframes.at(-1)?.wheels;
            if (!wheels) throw new Error('Missing episode target anchor arrangement.');
            if (segment.effects.some(effect => effect.effectKind === 'orbit')
                && (index !== replay.landmarks.findIndex(entry => entry.id === beat.landmark)
                    || segment.afterCue !== undefined)) throw new Error('Orbit cues currently require a stationary beat.');
            validatePrimitivePresentation({
                presentation: segment, wheels, frame: frameAt(index), emphasis,
                stationary: index === replay.landmarks.findIndex(entry => entry.id === beat.landmark),
            });
        }
    }
}

export function derivePrimitiveConceptGuidance(args: PrimitivePresentationContext & {
    beat: PrimitiveConceptBeat; playback: EpisodePlayback;
}): VisualGuidance | undefined {
    if (args.playback.error) return undefined;
    const { segment } = conceptPresentationAt(args.beat, args.playback);
    if (!segment) return undefined;
    const { playback } = args;
    return resolvePrimitiveConceptPresentation({
        ...createEpisodePresentationRequest(segment, playback, true),
        wheels: args.wheels, frame: args.frame, emphasis: args.emphasis,
    });
}
